import { useMemo, useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import {
  ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid,
  Customized, ResponsiveContainer,
  useXAxisScale, useYAxisScale,
} from 'recharts';
import { LAP_COLORS } from '../LapSelector/LapSelector';
import { convertValue, getDisplayUnit } from '../../utils/unitConvert';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import styles from './ScatterPlot.module.css';
import pickerStyles from './SpeedTrace.module.css';
import { useTheme } from '../../contexts/ThemeContext';
import { lastCursor } from '../../utils/chartCursor';
import { exportChartPng } from '../../utils/chartExport';

const M_TO_FT = 3.28084;

function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Invisible SVG circle used purely so Recharts' proximity detection still fires.
// fill="transparent" keeps the circle present in the DOM (vs fill="none" which
// removes it from pointer-event hit testing) while being visually invisible.
function HitDot({ cx, cy, r }) {
  return <circle cx={cx} cy={cy} r={r || 4} fill="transparent" />;
}

// Paints dots on a canvas overlay at full device-pixel resolution so they stay
// crisp at any size. Buffer is sized from canvas.offsetWidth/offsetHeight
// (actual CSS layout pixels) so it always matches the display box regardless
// of fractional DPR.
function drawCanvas(canvas, xScale, yScale, series, dotSize, hoverDistFt) {
  if (!canvas || !xScale || !yScale) return;

  const dpr  = window.devicePixelRatio || 1;
  const cssW = canvas.offsetWidth;
  const cssH = canvas.offsetHeight;
  if (!cssW || !cssH) return;

  canvas.width  = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);

  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  for (const s of series) {
    ctx.fillStyle = hexToRgba(s.color, 0.75);
    for (const pt of s.points) {
      const cx = xScale(pt.x);
      const cy = yScale(pt.y);
      if (!isFinite(cx) || !isFinite(cy)) continue;
      ctx.beginPath();
      ctx.arc(cx, cy, dotSize, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Draw a highlighted dot at the nearest data point to the hovered distance
  if (hoverDistFt != null) {
    for (const s of series) {
      let nearest = null, minDelta = Infinity;
      for (const pt of s.points) {
        if (pt.dist_ft == null) continue;
        const d = Math.abs(pt.dist_ft - hoverDistFt);
        if (d < minDelta) { minDelta = d; nearest = pt; }
      }
      if (!nearest) continue;
      const cx = xScale(nearest.x);
      const cy = yScale(nearest.y);
      if (!isFinite(cx) || !isFinite(cy)) continue;
      const r = dotSize + 3;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = s.color;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }
}

// Captures Recharts scale functions and triggers a canvas redraw whenever
// Recharts re-renders (data change, resize). Hover redraws bypass this
// component entirely — the mouse overlay below calls onDraw directly.
function CanvasDotsLayer({ scalesRef, onDraw }) {
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();

  useLayoutEffect(() => {
    if (xScale) scalesRef.current.xScale = xScale;
    if (yScale) scalesRef.current.yScale = yScale;
    onDraw();
  });

  return null;
}

// Recharts' native tooltip relies on per-dot DOM hit-testing (whichever tiny
// <circle> happens to be topmost in the SVG under the cursor). Where dots
// overlap densely, that pick depends on draw order, not proximity, so the
// reported values jump around as the mouse moves. Finding the true nearest
// point in pixel space instead keeps the readout stable.
const HOVER_MAX_PX = 25;

function findNearestPoint(px, py, series, xScale, yScale) {
  let best = null;
  let bestD2 = HOVER_MAX_PX * HOVER_MAX_PX;
  for (const s of series) {
    for (const pt of s.points) {
      const cx = xScale(pt.x);
      const cy = yScale(pt.y);
      if (!isFinite(cx) || !isFinite(cy)) continue;
      const dx = cx - px;
      const dy = cy - py;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) { bestD2 = d2; best = pt; }
    }
  }
  return best;
}

function dispatchHover(lat, lon, distFt) {
  window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
    detail: (lat != null && lon != null) ? { lat, lon, distFt: distFt ?? null } : null,
  }));
}

function downsample(arr, maxPts) {
  if (arr.length <= maxPts) return arr;
  const step = arr.length / maxPts;
  return Array.from({ length: maxPts }, (_, i) => arr[Math.round(i * step)]);
}

function fmtVal(v) {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1000) return Math.round(v).toLocaleString();
  if (abs >= 100)  return Math.round(v).toString();
  if (abs >= 10)   return v.toFixed(1);
  return v.toFixed(2);
}

/**
 * Build one scatter series per lap by joining the two channel traces index-by-index.
 * Both traces come from the same underlying lap telemetry rows so their lengths and
 * dist_m values align — no dist_m matching pass needed.
 * xTransform / yTransform are optional (value) => value unit-conversion functions.
 * zoomFt, when set, restricts points to [distFtMin, distFtMax].
 */
function buildSeries(
  channelTracesMap, xChannel, yChannel,
  selectedLaps, lapTraces,
  xTransform, yTransform,
  zoomFt = null,
  customColors = null,
  colors = LAP_COLORS,
  keyPrefix = 's1-',
  labelPrefix = '',
) {
  if (!xChannel || !yChannel) return [];

  return selectedLaps.map((lapIndex, i) => {
    const xTrace = channelTracesMap[xChannel]?.[lapIndex];
    const yTrace = channelTracesMap[yChannel]?.[lapIndex];
    if (!xTrace || !yTrace) return null;

    const xPts = xTrace.points ?? [];
    const yPts = yTrace.points ?? [];
    const len  = Math.min(xPts.length, yPts.length);
    if (!len) return null;

    const lapStartTime = xTrace.start_time ?? 0;
    const raw = [];
    for (let j = 0; j < len; j++) {
      const xp = xPts[j];
      const yp = yPts[j];
      if (xp.channel_value == null || yp.channel_value == null) continue;

      // Filter to the zoomed distance window when active
      if (zoomFt) {
        const ft = (xp.dist_m ?? 0) * M_TO_FT;
        if (ft < zoomFt[0] || ft > zoomFt[1]) continue;
      }

      raw.push({
        x:       xTransform ? xTransform(xp.channel_value) : xp.channel_value,
        y:       yTransform ? yTransform(yp.channel_value) : yp.channel_value,
        lat:      xp.lat ?? null,
        lon:      xp.lon ?? null,
        dist_ft:  (xp.dist_m ?? 0) * M_TO_FT,
        csv_time: lapStartTime + (xp.time ?? 0),
      });
    }
    if (!raw.length) return null;

    const sampled  = downsample(raw, 5000);
    const lapInfo  = lapTraces[lapIndex];
    const lapLabel = lapInfo?.lap_time_str || xTrace.lap_time_str || `L${lapIndex + 1}`;

    return {
      key:    `${keyPrefix}${lapIndex}`,
      color:  (customColors && customColors[lapIndex]) || colors[i % colors.length],
      label:  labelPrefix ? `${labelPrefix} ${lapLabel}` : lapLabel,
      points: sampled,
    };
  }).filter(Boolean);
}

// ── Axis channel picker ───────────────────────────────────────────────────────
// The same button → popover (filter + list) pattern as the Speed Trace and
// Multi-Channel pickers (whose CSS this borrows), single-select instead of
// checkboxes. Earlier this was a bare text input beside a native <select> per
// axis, which put four inputs in the header before Dot/PNG/legend had a say.

// Close a popover on any mousedown outside `ref`.
function useDismiss(ref, open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = e => { if (!ref.current?.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, [ref, open, onClose]);
}

// The popover body. Enter picks the first match so a filter-and-Enter round
// trip is the fast path; Escape clears the filter, then closes.
function ChannelMenu({ axis, channels, value, onPick, onClose, className }) {
  const [filter, setFilter] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 0); }, []);

  const needle   = filter.trim().toLowerCase();
  const filtered = needle ? channels.filter(ch => ch.name.toLowerCase().includes(needle)) : channels;

  return (
    <div className={`${pickerStyles.chDropdown} ${styles.menu} ${className || ''}`}>
      <div className={pickerStyles.chDropdownHdr}>{axis} axis channel</div>
      <div className={pickerStyles.chFilterWrap}>
        <input
          ref={inputRef}
          className={pickerStyles.chFilter}
          type="text"
          placeholder="Filter channels…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') { e.preventDefault(); if (filter) setFilter(''); else onClose(); }
            if (e.key === 'Enter' && filtered.length) { e.preventDefault(); onPick(filtered[0].name); }
          }}
        />
      </div>
      <div className={pickerStyles.chList}>
        {!filtered.length && <div className={pickerStyles.chNoMatch}>No channels match</div>}
        {filtered.map(ch => (
          <button
            type="button"
            key={ch.name}
            className={`${pickerStyles.chItem} ${styles.menuItem} ${ch.name === value ? styles.menuItemActive : ''}`}
            onClick={() => onPick(ch.name)}
          >
            <span className={pickerStyles.chItemName}>{ch.name}</span>
            {ch.unit && <span className={pickerStyles.chItemUnit}>{ch.unit}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// Header button for one axis. `open`/`onOpenChange` are owned by the parent so
// the axis labels on the plot can open the same menu (see .axisLabelX/Y).
function AxisPicker({ axis, value, channels, onChange, open, onOpenChange }) {
  const ref = useRef(null);
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  useDismiss(ref, open, close);

  return (
    <div className={`${pickerStyles.chPicker} ${styles.axisPicker}`} ref={ref}>
      <button
        type="button"
        className={`${pickerStyles.chPickerBtn} ${styles.axisPickerBtn}`}
        onClick={() => onOpenChange(!open)}
        title={`Choose the ${axis} axis channel`}
      >
        <span className={styles.axisLetter}>{axis}</span>
        <span className={styles.axisPickerValue}>{value || 'channel…'}</span>
        <span className={styles.axisPickerCaret}>▾</span>
      </button>
      {open && (
        <ChannelMenu
          axis={axis}
          channels={channels}
          value={value}
          onPick={name => { onChange(name); close(); }}
          onClose={close}
        />
      )}
    </div>
  );
}

// Rarely-touched settings live behind one ⚙ rather than taking header width.
function SettingsMenu({ dotSize, onDotSize }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  return (
    <div className={pickerStyles.chPicker} ref={ref}>
      <button
        type="button"
        className={styles.swapBtn}
        onClick={() => setOpen(v => !v)}
        title="Plot settings"
        aria-label="Plot settings"
      >⚙</button>
      {open && (
        <div className={`${pickerStyles.chDropdown} ${styles.settingsMenu}`}>
          <label className={styles.dotSizeLabel}>
            Dot size
            <input
              type="number"
              className={styles.dotSizeInput}
              value={dotSize}
              min={1}
              max={8}
              step={0.5}
              onChange={e => onDotSize(parseFloat(e.target.value) || 2)}
            />
            px
          </label>
        </div>
      )}
    </div>
  );
}

// ── Tooltip ───────────────────────────────────────────────────────────────────
// Positioned manually near the cursor by the mouse overlay (see hoverLayer)
// rather than through Recharts' built-in Tooltip component. It flips to the
// other side of the cursor past the midpoint of the chart on each axis: the
// panel clips its overflow, so a tooltip that always hung below-right was cut
// off whenever the data sat near the right or bottom edge.
const TOOLTIP_GAP = 12;
function CustomTooltip({ point, xChannel, yChannel, xUnit, yUnit }) {
  const { px, py, w, h } = point;
  const pos = {};
  if (px > w / 2) pos.right  = w - px + TOOLTIP_GAP; else pos.left = px + TOOLTIP_GAP;
  if (py > h / 2) pos.bottom = h - py + TOOLTIP_GAP; else pos.top  = py + TOOLTIP_GAP;
  return (
    <div className={styles.tooltip} style={pos}>
      <div className={styles.tooltipRow}>
        <span className={styles.tooltipCh}>{xChannel}</span>
        <span className={styles.tooltipVal}>
          {fmtVal(point.x)}{xUnit ? <span className={styles.tooltipUnit}> {xUnit}</span> : null}
        </span>
      </div>
      <div className={styles.tooltipRow}>
        <span className={styles.tooltipCh}>{yChannel}</span>
        <span className={styles.tooltipVal}>
          {fmtVal(point.y)}{yUnit ? <span className={styles.tooltipUnit}> {yUnit}</span> : null}
        </span>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ScatterPlot({
  availableChannels = [],
  channelTracesMap  = {},
  selectedLaps      = [],
  lapTraces         = {},
  compareDrivers    = [],
  onFetchChannel,
  unitPrefs,
  customColors1     = null,
  trackName         = '',
  sessionDate       = '',
  sessionVehicle    = '',
}) {
  const { chartColors } = useTheme();
  const [xChannel,    setXChannel]    = useState(null);
  const [yChannel,    setYChannel]    = useState(null);
  const [zoomDomain,  setZoomDomain]  = useState(null); // [distFtMin, distFtMax] | null
  // Which axis menu is open and where it was opened from: the header button
  // anchors the menu to itself; an axis label anchors it beside the label.
  const [openPicker,  setOpenPicker]  = useState(null); // { axis: 'X'|'Y', from: 'header'|'label' } | null
  const labelMenuRef = useRef(null);
  const closeLabelMenu = useCallback(() => setOpenPicker(p => (p?.from === 'label' ? null : p)), []);
  useDismiss(labelMenuRef, openPicker?.from === 'label', closeLabelMenu);
  const [dotSize,     setDotSize]     = useState(2);
  const [hoverPoint,  setHoverPoint]  = useState(null); // nearest point under the local cursor, for the tooltip
  const chartWrapRef = useRef(null);
  const canvasRef  = useRef(null);
  const scalesRef  = useRef({ xScale: null, yScale: null });
  const hoverRef   = useRef(null);
  const rafRef     = useRef(null);
  const moveRef    = useRef(null);
  const moveRafRef = useRef(null);
  // Keep refs in sync with latest render values so the rAF callbacks always
  // read current data without capturing stale closure values.
  const seriesRef  = useRef([]);
  const dotSizeRef = useRef(dotSize);
  dotSizeRef.current = dotSize;

  // Listen for zoom events from any trace chart
  useEffect(() => {
    const handler = (e) => {
      setZoomDomain(e.detail ? [e.detail.distFtMin, e.detail.distFtMax] : null);
    };
    window.addEventListener('trackapp:chart-zoom', handler);
    return () => window.removeEventListener('trackapp:chart-zoom', handler);
  }, []);

  // Sync cursor position from time-series charts via rAF — no React re-render
  useEffect(() => {
    const handler = (e) => {
      hoverRef.current = e.detail?.distFt ?? null;
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          const { xScale, yScale } = scalesRef.current;
          drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, hoverRef.current);
        });
      }
    };
    window.addEventListener('trackapp:chart-hover', handler);
    // Late mount (mobile tab switch): show the last cursor position.
    const last = lastCursor();
    if (last) handler({ detail: last });
    return () => {
      window.removeEventListener('trackapp:chart-hover', handler);
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, []);

  // Axes are picked by channel *name* and every loaded driver that logs a
  // channel of that name is plotted, so the menu lists each name once
  // (primary's entry first, then names only a comparison driver has).
  const pickerChannels = useMemo(() => {
    const seen = new Set();
    return availableChannels.filter(ch => !seen.has(ch.name) && seen.add(ch.name));
  }, [availableChannels]);

  // Fetch the X channel for every selected lap not yet loaded — the primary
  // session's laps from channelTracesMap, each comparison driver's from its own
  // per-slot cache (skipping drivers whose logger doesn't carry the channel).
  useEffect(() => {
    if (!xChannel || !onFetchChannel) return;
    selectedLaps.forEach(lapIndex => {
      if (!channelTracesMap[xChannel]?.[lapIndex]) {
        onFetchChannel(lapIndex, xChannel);
      }
    });
    compareDrivers.forEach(d => {
      if (!d.channels?.has(xChannel)) return;
      d.selectedLaps.forEach(lapIndex => {
        if (!d.channelTraces?.[xChannel]?.[lapIndex]) onFetchChannel(lapIndex, xChannel, d.slot);
      });
    });
  }, [xChannel, selectedLaps, onFetchChannel, channelTracesMap, compareDrivers]);

  // Same for Y.
  useEffect(() => {
    if (!yChannel || !onFetchChannel) return;
    selectedLaps.forEach(lapIndex => {
      if (!channelTracesMap[yChannel]?.[lapIndex]) {
        onFetchChannel(lapIndex, yChannel);
      }
    });
    compareDrivers.forEach(d => {
      if (!d.channels?.has(yChannel)) return;
      d.selectedLaps.forEach(lapIndex => {
        if (!d.channelTraces?.[yChannel]?.[lapIndex]) onFetchChannel(lapIndex, yChannel, d.slot);
      });
    });
  }, [yChannel, selectedLaps, onFetchChannel, channelTracesMap, compareDrivers]);

  // Raw units from the channel metadata (primary's entry comes first, so a
  // channel both sessions log takes the primary's unit for the axis label).
  const xRawUnit = availableChannels.find(c => c.name === xChannel)?.unit ?? '';
  const yRawUnit = availableChannels.find(c => c.name === yChannel)?.unit ?? '';

  // Display units after applying user prefs (falls back to raw unit if no conversion)
  const xUnit = unitPrefs ? getDisplayUnit(xRawUnit, unitPrefs) : xRawUnit;
  const yUnit = unitPrefs ? getDisplayUnit(yRawUnit, unitPrefs) : yRawUnit;

  // Value transform functions (null when no conversion applies)
  const xTransform = useMemo(
    () => unitPrefs && xRawUnit ? (v) => convertValue(v, xRawUnit, unitPrefs) : null,
    [xRawUnit, unitPrefs]
  );
  const yTransform = useMemo(
    () => unitPrefs && yRawUnit ? (v) => convertValue(v, yRawUnit, unitPrefs) : null,
    [yRawUnit, unitPrefs]
  );

  const series1 = useMemo(
    () => buildSeries(
      channelTracesMap, xChannel, yChannel,
      selectedLaps, lapTraces,
      xTransform, yTransform,
      zoomDomain, customColors1,
    ),
    [channelTracesMap, xChannel, yChannel, selectedLaps, lapTraces, xTransform, yTransform, zoomDomain, customColors1]
  );
  // Comparison drivers convert through their own logger's unit for the
  // channel — two sessions can log the same name in different units.
  const series2 = useMemo(
    () => compareDrivers.flatMap(d => {
      const unitFor = ch => availableChannels.find(c => c.source === d.slot && c.name === ch)?.unit ?? '';
      const xU = unitFor(xChannel), yU = unitFor(yChannel);
      return buildSeries(
        d.channelTraces || {}, xChannel, yChannel,
        d.selectedLaps, d.lapTraces || {},
        unitPrefs && xU ? v => convertValue(v, xU, unitPrefs) : null,
        unitPrefs && yU ? v => convertValue(v, yU, unitPrefs) : null,
        zoomDomain, d.customColors, d.palette, `s2-${d.slot}-`, d.name,
      );
    }),
    [compareDrivers, availableChannels, xChannel, yChannel, unitPrefs, zoomDomain]
  );
  const series = useMemo(() => [...series1, ...series2], [series1, series2]);
  seriesRef.current = series;

  // Stable callback passed to CanvasDotsLayer — reads current values from refs
  const handleCanvasDraw = useCallback(() => {
    const { xScale, yScale } = scalesRef.current;
    drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, hoverRef.current);
  }, []);

  const xAxisLabel = xChannel ? `${xChannel}${xUnit ? ` (${xUnit})` : ''}` : '';
  const yAxisLabel = yChannel ? `${yChannel}${yUnit ? ` (${yUnit})` : ''}` : '';

  // A transparent overlay div (see .hoverLayer) sits above the chart and canvas
  // and owns all mouse tracking, so the nearest point is always found by true
  // pixel distance rather than by whichever tiny SVG dot happens to be on top
  // in the DOM (see HOVER_MAX_PX comment above findNearestPoint).
  const handleInternalMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    moveRef.current = { px: e.clientX - rect.left, py: e.clientY - rect.top, w: rect.width, h: rect.height };
    if (moveRafRef.current) return;
    moveRafRef.current = requestAnimationFrame(() => {
      moveRafRef.current = null;
      const { xScale, yScale } = scalesRef.current;
      if (!xScale || !yScale || !moveRef.current) return;
      const { px, py, w, h } = moveRef.current;
      const nearest = findNearestPoint(px, py, seriesRef.current, xScale, yScale);
      setHoverPoint(nearest ? { x: nearest.x, y: nearest.y, px, py, w, h } : null);
      hoverRef.current = nearest?.dist_ft ?? null;
      drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, hoverRef.current);
      dispatchHover(nearest?.lat ?? null, nearest?.lon ?? null, nearest?.dist_ft ?? null);
    });
  }, []);

  // A click seeks the video (and every panel listening on chart-click) to the
  // dot under the cursor. The dot may belong to a comparison driver, whose
  // csv_time is on that driver's clock, so the video time comes from the
  // primary session's nearest sample by distance — the same rule SpeedTrace
  // applies — while the dot itself supplies the map position.
  const handleInternalClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const { xScale, yScale } = scalesRef.current;
    if (!xScale || !yScale) return;
    const nearest = findNearestPoint(e.clientX - rect.left, e.clientY - rect.top, seriesRef.current, xScale, yScale);
    if (!nearest || nearest.lat == null || nearest.lon == null) return;
    const distFt = nearest.dist_ft ?? null;
    let videoTime = nearest.csv_time ?? null;
    const primary = seriesRef.current.find(s => s.key.startsWith('s1-'));
    if (primary && distFt != null) {
      let best = null, bestD = Infinity;
      for (const pt of primary.points) {
        const d = Math.abs((pt.dist_ft ?? 0) - distFt);
        if (d < bestD) { bestD = d; best = pt; }
      }
      if (best?.csv_time != null) videoTime = best.csv_time;
    }
    window.dispatchEvent(new CustomEvent('trackapp:chart-click', {
      detail: { lat: nearest.lat, lon: nearest.lon, distFt, videoTime },
    }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    moveRef.current = null;
    setHoverPoint(null);
    hoverRef.current = null;
    const { xScale, yScale } = scalesRef.current;
    drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, null);
    dispatchHover(null, null);
  }, []);

  useEffect(() => () => {
    if (moveRafRef.current) cancelAnimationFrame(moveRafRef.current);
  }, []);

  const isReady   = xChannel && yChannel;
  const isLoading = isReady && series.length === 0
    && (selectedLaps.length > 0 || compareDrivers.some(d => d.selectedLaps.length > 0));

  // The dots live on the canvas, not in the SVG (whose Scatter dots are
  // transparent hit targets), so the export composites both: the SVG for axes
  // and grid, the canvas as an overlay. Redraw it without the hover highlight
  // first — a cursor on another panel could have left a ringed dot behind.
  const handleExportPng = () => {
    const svg = chartWrapRef.current?.querySelector('svg.recharts-surface');
    if (!svg || !canvasRef.current) return;
    const { xScale, yScale } = scalesRef.current;
    drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, null);
    const slug = value => String(value || '').replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '');
    const host = window.location.hostname;
    exportChartPng({
      svg,
      overlays: [canvasRef.current],
      axisLabels: { x: xAxisLabel, y: yAxisLabel },
      themeFrom: chartWrapRef.current,
      title: [trackName, sessionVehicle, sessionDate].filter(Boolean).join(' · ') || 'Scatter Plot',
      subtitle: `${yAxisLabel} vs ${xAxisLabel}${zoomDomain ? ' · zoomed' : ''}`,
      legend: series.map(s => ({ color: s.color, label: s.label })),
      plotTop: 10,
      plotBottom: 32,
      watermark: /^(localhost|127\.|\d+\.\d+\.)/.test(host) ? 'TrackDay Analysis' : host,
      filename: `${[slug(trackName) || 'track', slug(sessionDate), slug(yChannel), 'vs', slug(xChannel), 'scatter']
        .filter(Boolean).join('_')}.png`,
    })
      .catch(error => console.error('Chart export failed:', error))
      .finally(() => drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, hoverRef.current));
  };

  return (
    <div className={styles.panel}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>Scatter</span>}>
        {zoomDomain && (
          <button
            className={styles.zoomBadge}
            onClick={() => window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', { detail: null }))}
            title="Clear zoom"
          >↔ zoomed ×</button>
        )}

        <div className={styles.axisPickers}>
          <AxisPicker
            axis="X"
            value={xChannel}
            channels={pickerChannels}
            onChange={setXChannel}
            open={openPicker?.axis === 'X' && openPicker.from === 'header'}
            onOpenChange={o => setOpenPicker(o ? { axis: 'X', from: 'header' } : null)}
          />
          <button
            className={styles.swapBtn}
            onClick={() => { setXChannel(yChannel); setYChannel(xChannel); }}
            title="Swap X and Y axes"
            disabled={!xChannel && !yChannel}
          >⇄</button>
          <AxisPicker
            axis="Y"
            value={yChannel}
            channels={pickerChannels}
            onChange={setYChannel}
            open={openPicker?.axis === 'Y' && openPicker.from === 'header'}
            onOpenChange={o => setOpenPicker(o ? { axis: 'Y', from: 'header' } : null)}
          />
        </div>

        <div className={styles.headerRight}>
          <SettingsMenu dotSize={dotSize} onDotSize={setDotSize} />
          <button
            type="button"
            className={styles.swapBtn}
            onClick={handleExportPng}
            disabled={!isReady || series.length === 0}
            title="Download this scatter plot as a PNG image with a lap legend"
          >PNG ⤓</button>
        </div>
        <HelpButton panel="scatterplot" page="sessions" />
      </PanelHeader>

      <div className={styles.chartWrap} ref={chartWrapRef}>
        {!isReady ? (
          <div className={styles.empty}>Pick X and Y channels above to plot</div>
        ) : isLoading ? (
          <div className={styles.empty}>Loading…</div>
        ) : series.length === 0 ? (
          <div className={styles.empty}>
            {zoomDomain ? 'No data in the zoomed window' : 'No data for selected laps'}
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                accessibilityLayer={false}
                margin={{ top: 10, right: 20, bottom: 32, left: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                {/* Recharts' default numeric domain is [0, 'auto'], which
                    squashes a channel that lives far from zero (oil temp at
                    230–240 °F) against one edge. Fit both axes to the data. */}
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={['auto', 'auto']}
                  tickFormatter={fmtVal}
                  tick={{ fill: chartColors.tick, fontSize: 10 }}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  domain={['auto', 'auto']}
                  tickFormatter={fmtVal}
                  tick={{ fill: chartColors.tick, fontSize: 10 }}
                  width={56}
                />
                <Customized
                  component={CanvasDotsLayer}
                  scalesRef={scalesRef}
                  onDraw={handleCanvasDraw}
                />
                {series.map(s => (
                  <Scatter
                    key={s.key}
                    name={s.label}
                    data={s.points}
                    fill="transparent"
                    r={dotSize}
                    shape={HitDot}
                  />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
            <canvas ref={canvasRef} className={styles.dotsCanvas} />
            <div
              className={styles.hoverLayer}
              onMouseMove={handleInternalMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleInternalClick}
            />
            {hoverPoint && (
              <CustomTooltip
                point={hoverPoint}
                xChannel={xChannel}
                yChannel={yChannel}
                xUnit={xUnit}
                yUnit={yUnit}
              />
            )}

            {/* Axis labels are HTML over the chart margins rather than Recharts
                labels, so they can be clicked to change the channel. The PNG
                export draws them itself (see exportChartPng's axisLabels). */}
            <button
              type="button"
              className={styles.axisLabelX}
              onClick={() => setOpenPicker({ axis: 'X', from: 'label' })}
              title="Click to change the X axis channel"
            >{xAxisLabel} ▾</button>
            <button
              type="button"
              className={styles.axisLabelY}
              onClick={() => setOpenPicker({ axis: 'Y', from: 'label' })}
              title="Click to change the Y axis channel"
            >{yAxisLabel} ▾</button>
            {openPicker?.from === 'label' && (
              <div
                ref={labelMenuRef}
                className={openPicker.axis === 'X' ? styles.labelMenuX : styles.labelMenuY}
              >
                <ChannelMenu
                  axis={openPicker.axis}
                  channels={pickerChannels}
                  value={openPicker.axis === 'X' ? xChannel : yChannel}
                  onPick={name => { (openPicker.axis === 'X' ? setXChannel : setYChannel)(name); setOpenPicker(null); }}
                  onClose={() => setOpenPicker(null)}
                />
              </div>
            )}

            {/* Legend lives in the plot, not the header: it is the one header
                item that grows with lap count. pointer-events: none so hover
                tracking underneath still works. */}
            <div className={styles.legend}>
              {series.map(s => (
                <span key={s.key} className={styles.legendItem}>
                  <span className={styles.legendDot} style={{ background: s.color }} />
                  {s.label}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
